<template>
	<div class="header-global-search" @keydown.esc="closeGlobalSearch" ref="globalSearch">
		<div v-if="$route?.meta?.hasGlobalSearch" :class="['global-search-input-container', {'hide' : isSearchOpened}]">
			<img class="global-search-open" @click="openGlobalSearch" :src="require(`@/drc/assets/icons/search-glass.svg`)" alt="search">
			<input type="text" :placeholder="$t('globalSearchSections.searchPlaceholder')" @click="openGlobalSearch">
		</div>

		<div v-if="isSearchOpened" class="global-search-result-container">
			<div class="results-container">
				<div :class="['headline', {'extra-padding' : projects.length <= 4}]">
					<span class="spacer"></span>
					<div :class="['result-search']">
						<img class="global-search-open" @click="doGlobalSearch" :src="require(`@/drc/assets/icons/search-glass.svg`)" alt="search">
						<input
							type="text"
							:placeholder="$t('globalSearchSections.searchPlaceholder')"
							v-model="searchText"
							ref="autoFocusInput"
							@keydown.enter="doGlobalSearch"
						>
						<img
							class="global-search-close"
							v-if="searchText.trim() !== ''"
							@click="clearSearch"
							:src="require(`@/drc/assets/icons/x_black.svg`)" alt="x"
						>
					</div>
					<button @click="closeGlobalSearch">
						<img :src="require(`@/drc/assets/icons/x_black.svg`)" alt="x">
					</button>
				</div>

				<div class="result-body">
					<div class="suggestions-container">
						<h2 v-if="projects.length > 0">{{ $t('globalSearchSections.suggestions') }}</h2>
						<ul class="suggestions-list" v-if="projects.length > 0">
							<li
								v-for="([key, item], index) in Object.entries(searchData)"
								v-if="item.projects?.length > 0"
								:key="index"
								:class="[{'is-selected' : item.isSelected}]"
								@click="select(key)"
							>
								<span>{{$t(`globalSearchSections.${item.name}`)}}</span>
								<img :src="require('@/drc/assets/icons/arrows/arrow-up-right.svg')" alt="arrow">
							</li>
						</ul>
					</div>

					<div class="match-container">
						<h2>{{ $t('globalSearchSections.projects') }}</h2>
						<div class="match-projects-wrapper" v-if="projects.length > 0">
							<div :class="['result-projects-container', {'one-layout' : projects.length === 1}]">
								<ProjectCard v-for="(project, index) in projects" :key="index" :project="project" :forceGrid="true" />
							</div>
						</div>
						<div class="match-projects-wrapper" v-if="projects.length === 0">
							<div class="result-projects-container">
								<span>{{$t('globalSearchSections.noMatch')}}</span>
							</div>
						</div>
						<button
							v-if="projects.length > 0 && Object.entries(searchData).find(([key, value]) => value.isSelected)[1].hasMoreProjects"
							@click="exploreMore(Object.entries(searchData).find(([key, value]) => value.isSelected)[1])"
						>
							<span>{{$t('homePage.exploreAllProjects')}}</span>
							<img class="arrow" src="@/drc/assets/images/round-arrow-right.svg" alt="arrow-right" />
							<img class="arrow-hover" src="@/drc/assets/images/round-arrow-right-hover-transparent.svg" alt="arrow-right" />
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>

import ProjectCard from "@/drc/components/ProjectCard";
import {
	getByText, getByToolName, getByCertificateName, getByCompanyName, getByBusinessManagerName,
	getByDeliveryManagerName, getByOwnerName, getByRelatedOffersTextContaining
} from "@/drc/services/global-search-service"
import * as STATISTIC_SERVICE from "@/common/services/statistic-service";
import DrcCommonLoadingMixin from "@/common/mixins/drc-common-loading-mixin";
import {EventSideMenu} from "@/common/configurations/event-bus";

export default {
	name: "HeaderGlobalSearch",
	mixins: [DrcCommonLoadingMixin],
	components: {
		ProjectCard
	},
	props: {
		latestProjects: {
			type: Array,
			required: true
		}
	},
	data() {
		return {
			isSearchOpened: false,
			searchText: '',
			projects: [],
			searchData: null
		}
	},
	methods: {
		openGlobalSearch() {
			this.closeSideMenu();
			this.isSearchOpened = true;
			this.setInitialSearchData();
			this.projects = this.searchData.top6Projects.projects;

			this.$nextTick(() => {
				if (this.$refs.autoFocusInput) {
					this.$refs.autoFocusInput.focus();
				}
			});
			this.openGlobalSearchStatistics();
		},
		setInitialSearchData() {
			this.searchData = {
				top6Projects: {
					projects: this.latestProjects,
					isSelected: true,
					hasMoreProjects: true,
					name: `latestProjects`,
					filtersName: null,
					resetOnClose: false
				},
				projectsByText: {
					projects: [],
					isSelected: false,
					hasMoreProjects: false,
					name: `projectTextContentMatch`,
					filtersName: null,
					resetOnClose: true
				},
				projectsByCompany: {
					projects: [],
					isSelected: false,
					hasMoreProjects: false,
					name: `clientNameMatch`,
					filtersName: 'companies',
					resetOnClose: true
				},
				projectsByTool: {
					projects: [],
					isSelected: false,
					hasMoreProjects: false,
					name: `toolMatch`,
					filtersName: 'tools',
					resetOnClose: true
				},
				projectsByCertification: {
					projects: [],
					isSelected: false,
					hasMoreProjects: false,
					name: `certificateMatch`,
					filtersName: 'certificates',
					resetOnClose: true
				},
				projectsByOwner: {
					projects: [],
					isSelected: false,
					hasMoreProjects: false,
					name: `ownerMatch`,
					filtersName: 'referenceOwners',
					resetOnClose: true
				},
				projectsByBusinessContactManager: {
					projects: [],
					isSelected: false,
					hasMoreProjects: false,
					name: `businessManagerMatch`,
					filtersName: 'businessContactManagers',
					resetOnClose: true
				},
				projectsByDeliveryContactManager: {
					projects: [],
					isSelected: false,
					hasMoreProjects: false,
					name: `deliveryManagerMatch`,
					filtersName: 'deliveryContactManagers',
					resetOnClose: true
				},
				projectsByRelatedOffers: {
					projects: [],
					isSelected: false,
					hasMoreProjects: false,
					name: `relatedOfferMatch`,
					filtersName: 'relatedOffers',
					resetOnClose: true
				},
			}
		},
		closeGlobalSearch() {
			this.searchText = '';
			this.isSearchOpened = false;
		},
		doGlobalSearch() {
			if (this.searchText.trim() === '') {
				this.clearSearch();
				return;
			}

			this.$loader.show({text: '', description: '', buttonText: null, isBlue: false, onCancel: () => {}});
			const requests = [
				getByText(this.searchText, this.$i18n.locale),
				getByToolName(this.searchText, this.$i18n.locale),
				getByCertificateName(this.searchText, this.$i18n.locale),
				getByCompanyName(this.searchText, this.$i18n.locale),
				getByBusinessManagerName(this.searchText, this.$i18n.locale),
				getByDeliveryManagerName(this.searchText, this.$i18n.locale),
				getByOwnerName(this.searchText, this.$i18n.locale),
				getByRelatedOffersTextContaining(this.searchText)
			];

			Promise.all(requests)
				.then(results => {
					results.forEach((result) => {
						if (result.error) {
							console.error(result.error)
						} else {
							if (result.name === 'projectsByRelatedOffers') {
								this.searchData[result.name].projects = result.payload;
								this.searchData[result.name].hasMoreProjects = false;
							} else {
								this.searchData[result.name].projects = result.payload.slice(0, 6);
								this.searchData[result.name].hasMoreProjects = result.payload.length > 6;
							}
						}
					});
					this.setResultVisibility();

					this.$loader.hide();
				})
				.finally(() => {
					this.addGlobalSearchStatistic();
				});
		},
		setResultVisibility() {
			this.searchData.top6Projects.projects = [];
			let selected = null
			Object.entries(this.searchData).forEach(([key, value]) => {
				if (!selected && key !== 'top6Projects' && value.projects.length > 0) {
					selected = key;
				}
			})

			if (selected) {
				this.select(selected);
			} else {
				Object.values(this.searchData).forEach(item => item.isSelected = false);
				this.projects = [];
			}
		},
		clearSearch() {
			this.$loader.show({text: '', description: '', buttonText: null, isBlue: false, onCancel: () => {}});
			this.searchText = ''
			this.searchData.top6Projects.projects = this.latestProjects;
			this.select('top6Projects')
			Object.entries(this.searchData).forEach(([key, value]) => {
				if (this.searchData.top6Projects !== value) {
					this.searchData[key].projects = [];
				}
			});
			this.$loader.hide();
		},
		select(name) {
			Object.values(this.searchData).forEach(item => item.isSelected = false);
			this.searchData[name].isSelected = true;
			this.projects = this.searchData[name].projects;
		},
		exploreMore(selected) {
			let result = `/projects/search?language=${this.$i18n.locale}&sort=management_firstDatePublish,DESC&size=20&page=0&view=GRID`
			if (!selected.filtersName) {
				if (this.searchText.trim() !== '') {
					result = result + `&text=${this.searchText}`
				}
			} else {
				let filters = this.$store.getters.getAllCommonData[selected.filtersName].filter(item => this.getFilterName(item).toLowerCase().includes(this.searchText.toLowerCase()));
				filters = filters.map(result => result.id)
				result = result + `&${selected.filtersName}=${filters.join(',')}`
			}
			this.closeGlobalSearch();
			this.$router.push(result)
		},
		getFilterName(option) {
			if (option.translations) {
				return option.translations.find(translation => translation.language.name === this.$i18n.locale).name
			} else if (option.label) {
				return option.label
			} else if (option.name) {
				return option.name
			}  else {
				return null;
			}
		},
		openGlobalSearchStatistics(){
			const now = new Date();
			this.$gtag.event("open_global_search", {
				event_type: "action",
				event_category: "drc",
				event_page: "home",
				event_label: "Open global search on home page ",
				event_location: this.$route.name,
				event_path: this.$route.path,
				user_id: this.$store.getters.getUser.id,
				user_name: this.$store.getters.getUser.fullName,
				user_email: this.$store.getters.getUser.email,
				year: now.getFullYear(),
				month: now.getMonth() + 1,
				day: now.getDate(),
				time: new Date().toJSON().split('T')[1].split('.')[0],
			});
			const props = {
				action: 'open_global_search',
				comment: JSON.stringify({search_type: "GLOBAL_SEARCH_OPEN", keyword: `${this.searchText}`})
			}
			STATISTIC_SERVICE.addDrcStatistic(props);
		},
		addGlobalSearchStatistic() {
			const now = new Date();
			this.$gtag.event("SEARCH_ON_HOME_PAGE", {
				event_type: "search",
				event_category: "drc",
				event_page: "home",
				event_label: "Global search on home page",
				event_location: this.$route.name,
				event_path: this.$route.path,
				user_id: this.$store.getters.getUser.id,
				user_name: this.$store.getters.getUser.fullName,
				user_email: this.$store.getters.getUser.email,
				year: now.getFullYear(),
				month: now.getMonth() + 1,
				day: now.getDate(),
				time: new Date().toJSON().split('T')[1].split('.')[0],
				search_term: this.searchText
			});
			const props = {
				action: "SEARCH_ON_HOME_PAGE",
				comment: JSON.stringify({search_type: "GLOBAL_SEARCH", keyword: `${this.searchText}`})
			}
			STATISTIC_SERVICE.addDrcStatistic(props);
		},
		closeSideMenu() {
			let sideNavigation = document.getElementsByClassName("sidenav-container")[0];
			if (sideNavigation.classList.contains("active")) {
				EventSideMenu.$emit("toggle-sidemenu");
				EventSideMenu.$emit("toggle-burger-logo");
			}
		}
	},
}
</script>

<style lang="scss" scoped>
@import "@/common/assets/css/_variables.scss";

.header-global-search {
	flex-grow: 1;
	max-width: 620px;

	.global-search-input-container {
		width: 100%;
		height: 100%;
		max-height: 45px;
		display: flex;
		flex-direction: row;
		justify-content: center;
		align-items: center;
		position: relative;
		cursor: pointer;

		&.hide {
			visibility: hidden;
		}

		input {
			width: 100%;
			height: 100%;
			padding: 10px 46px;
			background: #193547;
			color: white;
			font-family: Roboto, sans-serif;
			font-weight: 400;
			font-size: 16px;
			line-height: 21px;
			letter-spacing: 0;
			border: 2px solid #3C3C432E;

			&:focus {
				outline: none;
			}

			&::placeholder {
				color: #EBEBF599;
			}
		}

		.global-search-open {
			position: absolute;
			left: 0;
			padding-left: 12px;
			padding-right: 12px;
			filter: brightness(0) saturate(100%) invert(100%) sepia(12%) saturate(7496%) hue-rotate(206deg) brightness(113%) contrast(107%);
		}

	}

	.global-search-result-container {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100vh;
		max-height: 100vh;
		padding: 54px 6% 20px;
		background: rgba(0, 0, 0, 0.5019607843);
		-webkit-backdrop-filter: blur(5.6000003815px);
		backdrop-filter: blur(5.6000003815px);
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		justify-content: flex-end;
		z-index: 5;
		overflow: auto;
		scrollbar-width: none;

		@media (max-width: 750px) {
			scrollbar-width: thin;
			scrollbar-color: #FFB81C transparent;
		}

		.results-container {
			width: 100%;
			position: relative;
			top: 0;
			z-index: 4;
			max-width: 1440px;
			margin: 0 auto;
			display: flex;
			flex-direction: column;
			margin-top: 26px;

			.headline {
				display: flex;
				flex-direction: row;
				position: relative;
				justify-content: flex-end;
				gap: 25px;

				&.extra-padding {
					padding-right: 10px;
				}

				.spacer {
					display: flex;
					flex-direction: column;
					flex-basis: 27%;
					min-width: 300px;

					@media (max-width: 750px) {
						display: none;
					}
				}

				.result-search {
					width: 100%;
					max-height: 45px;
					display: flex;
					flex-direction: row;
					justify-content: center;
					align-items: center;
					position: relative;
					cursor: pointer;
					margin-bottom: 25px;
					flex-basis: 73%;

					@media (max-width: 750px) {
						flex-basis: 100%;
					}

					input {
						width: 100%;
						height: 100%;
						padding: 10px 46px;
						background: white;
						color: black;
						font-family: Roboto, sans-serif;
						font-weight: 400;
						font-size: 16px;
						line-height: 21px;
						letter-spacing: 0;
						border: 2px solid #3C3C432E;

						&:focus {
							outline: none;
							border: 2px solid #D99C15;
						}

						&::placeholder {
							color: grey;
						}
					}

					.global-search-open {
						position: absolute;
						left: 0;
						padding-left: 12px;
						padding-right: 12px;
						filter: brightness(0) saturate(100%) invert(0%) sepia(4%) saturate(16%) hue-rotate(152deg) brightness(98%) contrast(108%);
					}

					.global-search-close {
						position: absolute;
						height: 100%;
						width: 35px;
						padding: 12px 12px;
						top: 0;
						right: 0;
						filter: brightness(0) saturate(100%) invert(0%) sepia(4%) saturate(16%) hue-rotate(152deg) brightness(98%) contrast(108%);
					}
				}

				button {
					background: transparent;
					padding: 12px;
					cursor: pointer;
					border-radius: 4px;
					border: 1px solid rgba(235, 235, 245, 0.1803921569);
					position: absolute;
					right: 0;
					top: -62px;

					img {
						filter: brightness(0) saturate(100%) invert(100%) sepia(7%) saturate(0%) hue-rotate(100deg) brightness(107%) contrast(106%);
					}

					&:hover {
						border: 1px solid white;
					}
				}
			}

			.result-body {
				display: flex;
				flex-direction: row;
				gap: 25px;

				@media (max-width: 750px) {
					flex-direction: column;
				}

				div > h2 {
					margin-top: 0;
					font-family: Roboto, sans-serif;
					font-weight: 400;
					font-size: 13px;
					line-height: 18px;
					letter-spacing: 0;
					text-transform: uppercase;
					color: white;
					margin-bottom: 14px;
				}

				.suggestions-container {
					display: flex;
					flex-direction: column;
					flex-basis: 27%;
					min-width: 300px;
					height: fit-content;
					position: relative;

					@media (max-width: 750px) {
						min-width: unset;
					}

					ul {
						padding-left: 0;
						display: flex;
						flex-direction: column;
						gap: 16px;
						margin: 0;

						li {
							list-style-type: none;
							display: flex;
							flex-direction: row;
							flex-wrap: nowrap;
							gap: 10px;
							cursor: pointer;

							span {
								font-family: Roboto, sans-serif;
								font-weight: 400;
								font-size: 22px;
								line-height: 28px;
								letter-spacing: 0.35px;
								vertical-align: middle;
								color: white;
								transition: color 0.6s ease-in-out;
							}

							img {
								filter: brightness(0) saturate(100%) invert(97%) sepia(11%) saturate(0%) hue-rotate(44deg) brightness(116%) contrast(100%);
								height: 28px;
								width: 28px;
								opacity: 0;
								transition: opacity 0.6s ease-in-out;
							}

							&:hover {
								span {
									color: #FFB81C;
								}

								img {
									opacity: 1;
									filter: brightness(0) saturate(100%) invert(86%) sepia(22%) saturate(6757%) hue-rotate(342deg) brightness(107%) contrast(103%);
								}
							}

							&.is-selected {
								span {
									color: #FFB81C;
								}

								img {
									opacity: 1;
									filter: brightness(0) saturate(100%) invert(86%) sepia(22%) saturate(6757%) hue-rotate(342deg) brightness(107%) contrast(103%);
								}
							}

						}
					}
				}

				.match-container {
					display: flex;
					flex-direction: column;
					flex-basis: 73%;
					height: 100%;

					.match-projects-wrapper {
						overflow: auto;
						max-height: calc(100vh - 270px);
						height: fit-content;
						scrollbar-width: thin;
						padding-right: 10px;
						scrollbar-color: #FFB81C transparent;

						@media (max-width: 750px) {
							overflow: unset;
							max-height: unset;
						}

						.result-projects-container {
							display: grid;
							grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
							grid-auto-rows: minmax(auto, 380px);
							gap: 24px;

							&.one-layout {
								grid-template-columns: repeat(auto-fit, minmax(310px, 400px));
							}

							span {
								display: flex;
								align-items: center;
								font-size: 23px;
							}
						}
					}

					button {
						border: none;
						cursor: pointer;
						padding: 10px 0 10px 10px;
						background: transparent;
						width: fit-content;
						align-self: flex-end;
						display: flex;
						flex-direction: row;
						justify-content: center;
						align-items: center;
						margin-top: 25px;

						span {
							font-family: Roboto, serif;
							font-weight: 400;
							font-size: 20px;
							line-height: 25px;
							letter-spacing: 0.38px;
							color: white;
						}

						.arrow {
							margin-left: 10px;
							display: inline;
							filter: brightness(0) saturate(100%) invert(97%) sepia(11%) saturate(0%) hue-rotate(44deg) brightness(116%) contrast(100%);
						}

						.arrow-hover {
							display: none;
						}

						&:hover {
							span {
								text-decoration: underline;
							}

							.arrow-hover {
								margin-left: 10px;
								display: inline;
								filter: brightness(0) saturate(100%) invert(97%) sepia(11%) saturate(0%) hue-rotate(44deg) brightness(116%) contrast(100%);
							}

							.arrow {
								display: none;
							}
						}
					}
				}
			}

		}
	}
}
</style>
